import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    hcaptcha?: {
      render: (container: HTMLElement | string, opts: HCaptchaOptions) => string;
      reset: (id?: string) => void;
      execute: (id?: string) => void;
    };
  }
}

interface HCaptchaOptions {
  sitekey: string;
  callback: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
  size?: 'normal' | 'compact' | 'invisible';
  theme?: 'light' | 'dark';
}

interface HCaptchaProps {
  sitekey: string;
  onVerify: (token: string) => void;
  onExpire?: () => void;
}

const SCRIPT_SRC = 'https://js.hcaptcha.com/1/api.js?render=explicit';
let scriptLoading: Promise<void> | null = null;

function loadHCaptchaScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.hcaptcha) return Promise.resolve();
  if (scriptLoading) return scriptLoading;
  scriptLoading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('hCaptcha script failed to load'));
    document.head.appendChild(script);
  });
  return scriptLoading;
}

export function HCaptcha({ sitekey, onVerify, onExpire }: HCaptchaProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadHCaptchaScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.hcaptcha) return;
        widgetIdRef.current = window.hcaptcha.render(containerRef.current, {
          sitekey,
          callback: onVerify,
          'expired-callback': onExpire,
          'error-callback': onExpire,
        });
      })
      .catch(() => {
        // Surface a fallback so the form can still submit during outages.
        if (!cancelled) onVerify('');
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.hcaptcha) {
        try {
          window.hcaptcha.reset(widgetIdRef.current);
        } catch {
          // ignore — widget may have been removed
        }
      }
    };
  }, [sitekey, onVerify, onExpire]);

  return <div ref={containerRef} />;
}
